/**
 * VisualEditorModal — extracted from TeacherDashboard.js (Feb 2026 refactor).
 *
 * Lightbox the teacher opens from a section's "Find visuals" button. It
 * fetches web images contextual to the lesson + section title, lets the
 * teacher attach any of them as section images, and surfaces the existing
 * generated + attached images for quick removal.
 *
 * The component is otherwise dependency-free: every backend call is
 * routed through the `api` prop passed in by ContentEditor, so this file
 * has no implicit coupling to `localStorage` / token plumbing.
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { X, RefreshCw, Image, Image as ImageIcon, Sparkles, Wand2, Plus, Check, CheckCircle, Trash2, ArrowDown } from 'lucide-react';
import { Button } from './../../ui/button';

// Visual Editor Modal — fetches web images for lesson context
function VisualEditorModal({ section, onClose, api, draftId, draftTitle, onGenerateImage, isGenerating, onImageAttached, onRemoveSectionImage, onRemoveGeneratedImage }) {
  const [webImages, setWebImages] = useState([]);
  const [loadingWeb, setLoadingWeb] = useState(false);
  const [addedUrls, setAddedUrls] = useState(new Set((section.section_images || []).map(i => i.url)));
  // Which AI-image-prompt card is generating right now — `isGenerating` from
  // the parent is one flag shared by the whole section, so without this every
  // card would show "Generating..." together; this tracks the one actually
  // clicked so only its own button reflects the in-flight request.
  const [generatingIdx, setGeneratingIdx] = useState(null);

  const fetchWebImages = async () => {
    setLoadingWeb(true);
    try {
      const query = `${draftTitle} ${section.title}`;
      const res = await api(`/studio/search-web-images`, { method: 'POST', body: JSON.stringify({ query }) });
      if (res.images) setWebImages(res.images);
    } catch { /* silent */ }
    setLoadingWeb(false);
  };

  useEffect(() => { fetchWebImages(); }, []);

  const attachImage = async (url, caption) => {
    try {
      await api(`/studio/framework/${draftId}/section/${section.id}/attach-image`, {
        method: 'POST', body: JSON.stringify({ image_url: url, caption })
      });
      setAddedUrls(prev => new Set([...prev, url]));
      onImageAttached(url, caption);
      toast.success('Image added to section');
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="visual-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 p-5 border-b border-gray-100" style={{ background: 'linear-gradient(90deg, #f0fdf4, #f0f9ff)' }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#34A853]/10"><Image className="w-5 h-5 text-[#34A853]" /></div>
          <div className="flex-1">
            <h3 className="text-lg font-extrabold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{section.title}</h3>
            <p className="text-xs text-gray-500">Visual Aids — Web images in context to your lesson</p>
          </div>
          <button onClick={fetchWebImages} disabled={loadingWeb} className="text-xs font-bold text-[#34A853] px-3 py-1.5 rounded-lg hover:bg-[#34A853]/10 flex items-center gap-1"><RefreshCw className={`w-3.5 h-3.5 ${loadingWeb ? 'animate-spin' : ''}`} /> Refresh</button>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Existing section images */}
          {(section.section_images || []).length > 0 && (
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Current Section Images ({section.section_images.length})</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {section.section_images.map((img, i) => (
                  <div key={i} className="rounded-xl overflow-hidden border border-gray-200 shadow-sm group/img relative" data-testid={`sec-img-${i}`}>
                    <img src={img.url} alt={img.caption || ''} className="w-full aspect-video object-cover bg-gray-50" onError={e => { e.target.closest('[data-testid]').style.display = 'none'; }} />
                    {img.caption && <p className="text-[10px] text-gray-500 p-2 bg-gray-50">{img.caption}</p>}
                    <button onClick={() => onRemoveSectionImage(i)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-opacity shadow-md hover:bg-red-600" data-testid={`delete-sec-img-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Generated images */}
          {(section.generated_images || []).length > 0 && (
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">AI Generated</p>
              <div className="grid grid-cols-2 gap-4">
                {section.generated_images.map((img, i) => (
                  <div key={i} className="rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-shadow relative group" data-testid={`gen-img-${section.id}-${i}`}>
                    <img src={img.url} alt="" className="w-full object-contain max-h-[350px] bg-gray-50" onError={e => { e.target.closest('[data-testid]').style.display = 'none'; }} />
                    <button onClick={() => onRemoveGeneratedImage(i)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-red-600" data-testid={`delete-gen-img-${section.id}-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Web images — contextual suggestions */}
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Suggested Web Images</p>
            {loadingWeb ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[1,2,3].map(i => <div key={i} className="aspect-video rounded-xl bg-gray-100 animate-pulse" />)}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {webImages.map((img, i) => (
                  <div key={i} className="rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-all group/web">
                    <div className="relative aspect-video bg-gray-50">
                      <img src={img.url} alt={img.description} className="w-full h-full object-cover" loading="lazy" onError={e => { e.target.style.display = 'none'; }} />
                      {!addedUrls.has(img.url) ? (
                        <button onClick={() => attachImage(img.url, img.description)}
                          className="absolute inset-0 bg-black/0 group-hover/web:bg-black/40 flex items-center justify-center opacity-0 group-hover/web:opacity-100 transition-all"
                          data-testid={`add-web-img-${i}`}>
                          <span className="text-white text-xs font-bold px-4 py-2 rounded-lg" style={{ background: 'linear-gradient(135deg, #34A853, #0d9488)' }}><Plus className="w-3 h-3 inline mr-1" /> Add to Section</span>
                        </button>
                      ) : (
                        <div className="absolute top-2 right-2"><span className="text-[9px] font-bold text-white bg-[#34A853] px-2 py-1 rounded-full"><CheckCircle className="w-3 h-3 inline mr-0.5" /> Added</span></div>
                      )}
                    </div>
                    <div className="p-2.5">
                      <p className="text-[11px] font-bold text-gray-700 truncate">{img.description}</p>
                      <span className="text-[9px] font-bold uppercase text-gray-400">{img.type}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Flowchart prompts */}
          {(section.image_prompts || []).length > 0 && (
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">AI Image Prompts</p>
              <div className="flex flex-col items-center gap-3 py-2">
                {section.image_prompts.map((p, i) => (
                  <React.Fragment key={i}>
                    <div className="w-full max-w-lg bg-white rounded-2xl border-2 border-gray-200 p-5 text-center shadow-sm hover:shadow-lg hover:border-[#34A853]/40 transition-all">
                      <p className="text-sm font-bold text-gray-900 mb-1.5">{p.label}</p>
                      <p className="text-xs text-gray-500 leading-relaxed mb-3">{p.prompt}</p>
                      <Button
                        onClick={async () => {
                          setGeneratingIdx(i);
                          try { await onGenerateImage(p.prompt); } finally { setGeneratingIdx(null); }
                        }}
                        disabled={isGenerating}
                        className="h-9 px-5 rounded-xl text-white text-xs font-bold"
                        style={{ background: 'linear-gradient(135deg, #34A853, #0d9488)' }}>
                        <Sparkles className="w-3.5 h-3.5 mr-1.5" />{generatingIdx === i ? 'Generating...' : 'Generate Image'}
                      </Button>
                    </div>
                    {i < section.image_prompts.length - 1 && <ArrowDown className="w-4 h-4 text-gray-300" />}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default VisualEditorModal;
